// Content Reveal Animation using GSAP and ScrollTrigger
// Supports grouped reveals with nested groups and configurable stagger/distance
//
// Data Attributes:
// - data-reveal-group="scroll|load" - Required to enable group reveal (default: scroll)
// - data-stagger="100" - Stagger between items in milliseconds (default: 100)
// - data-distance="2em" - Distance elements travel when revealing (default: 2em)
// - data-start="top 80%" - ScrollTrigger start position (default: top 80%)
// - data-delay="0.5" - Delay in seconds before animation starts after trigger (default: 0)
// - data-reveal-group-nested - Marks a nested group within a parent group
// - data-ignore="false" - Include parent element in animation when using nested groups
//
// Note: Animations are disabled on mobile (< 768px) for performance

;(function () {
  gsap.registerPlugin(ScrollTrigger)

  const contexts = new WeakMap()

  // Show elements immediately without animation (for mobile)
  function showElementsImmediately(scope = document) {
    scope.querySelectorAll('[data-reveal-group]').forEach((groupEl) => {
      gsap.set(groupEl, { clearProps: 'all', y: 0, autoAlpha: 1 })

      // Also show all children
      const directChildren = Array.from(groupEl.children).filter(
        (el) => el.nodeType === 1,
      )
      directChildren.forEach((child) => {
        gsap.set(child, { clearProps: 'all', y: 0, autoAlpha: 1 })

        // Handle nested groups
        const nestedGroup = child.matches('[data-reveal-group-nested]')
          ? child
          : child.querySelector(':scope [data-reveal-group-nested]')

        if (nestedGroup) {
          Array.from(nestedGroup.children).forEach((nestedChild) => {
            gsap.set(nestedChild, { clearProps: 'all', y: 0, autoAlpha: 1 })
          })
        }
      })
    })
  }

  function initContentRevealScroll(scope = document) {
    if (contexts.has(scope)) {
      contexts.get(scope).revert()
    }

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    const ctx = gsap.context(() => {
      // Use matchMedia to only run animations on desktop (768px+)
      const mm = gsap.matchMedia()

      // Desktop: Run full animations
      mm.add('(min-width: 768px)', () => {
        scope.querySelectorAll('[data-reveal-group]').forEach((groupEl) => {
          // Config from attributes or defaults (group-level)
          const triggerType =
            groupEl.getAttribute('data-reveal-group') || 'scroll'
          const groupStaggerSec =
            (parseFloat(groupEl.getAttribute('data-stagger')) || 200) / 1000 // ms → sec
          const groupDistance = groupEl.getAttribute('data-distance') || '2rem'
          const triggerStart =
            groupEl.getAttribute('data-start') || 'clamp(top 80%)'
          const groupDelay = parseFloat(groupEl.getAttribute('data-delay')) || 0

          const animDuration = 1
          const animEase = 'expo.out'

          // Reduced motion: show immediately
          if (prefersReduced) {
            gsap.set(groupEl, { clearProps: 'all', y: 0, autoAlpha: 1 })
            return
          }

          // If no direct children, animate the group element itself
          const directChildren = Array.from(groupEl.children).filter(
            (el) => el.nodeType === 1,
          )
          if (!directChildren.length) {
            gsap.set(groupEl, { y: groupDistance, autoAlpha: 0 })

            const animateSelf = () =>
              gsap.to(groupEl, {
                y: 0,
                autoAlpha: 1,
                duration: animDuration,
                ease: animEase,
                delay: groupDelay,
                onComplete: () => gsap.set(groupEl, { clearProps: 'all' }),
              })

            if (triggerType === 'load') {
              animateSelf()
            } else {
              ScrollTrigger.create({
                trigger: groupEl,
                start: triggerStart,
                once: true,
                onEnter: animateSelf,
              })
            }
            return
          }

          // Build animation slots: item or nested (deep layers allowed)
          const slots = []
          for (const child of directChildren) {
            const nestedGroup = child.matches('[data-reveal-group-nested]')
              ? child
              : child.querySelector(':scope [data-reveal-group-nested]')

            if (nestedGroup) {
              const includeParent =
                child.getAttribute('data-ignore') === 'false' ||
                nestedGroup.getAttribute('data-ignore') === 'false'
              slots.push({
                type: 'nested',
                parentEl: child,
                nestedEl: nestedGroup,
                includeParent,
              })
            } else {
              slots.push({ type: 'item', el: child })
            }
          }

          // Initial hidden state
          for (const slot of slots) {
            if (slot.type === 'item') {
              // If the element itself is a nested group, force group distance (prevents it from using its own data-distance)
              const isNestedSelf = slot.el.matches('[data-reveal-group-nested]')
              const d = isNestedSelf
                ? groupDistance
                : slot.el.getAttribute('data-distance') || groupDistance
              gsap.set(slot.el, { y: d, autoAlpha: 0 })
            } else {
              // Parent follows the group's distance when included, regardless of nested's data-distance
              if (slot.includeParent) {
                gsap.set(slot.parentEl, { y: groupDistance, autoAlpha: 0 })
              }
              // Children use nested group's own distance (fallback to group distance)
              const nestedD =
                slot.nestedEl.getAttribute('data-distance') || groupDistance
              for (const target of slot.nestedEl.children) {
                gsap.set(target, { y: nestedD, autoAlpha: 0 })
              }
            }
          }

          // Extra safety: if a nested parent is included, re-assert its distance to the group's value
          for (const slot of slots) {
            if (slot.type === 'nested' && slot.includeParent) {
              gsap.set(slot.parentEl, { y: groupDistance })
            }
          }

          // Reveal sequence
          const animateSlots = () => {
            const tl = gsap.timeline()

            slots.forEach((slot, slotIndex) => {
              const slotTime = groupDelay + slotIndex * groupStaggerSec

              if (slot.type === 'item') {
                tl.to(
                  slot.el,
                  {
                    y: 0,
                    autoAlpha: 1,
                    duration: animDuration,
                    ease: animEase,
                    onComplete: () => gsap.set(slot.el, { clearProps: 'all' }),
                  },
                  slotTime,
                )
              } else {
                // Optionally include the parent at the same slot time (parent uses group distance)
                if (slot.includeParent) {
                  tl.to(
                    slot.parentEl,
                    {
                      y: 0,
                      autoAlpha: 1,
                      duration: animDuration,
                      ease: animEase,
                      onComplete: () =>
                        gsap.set(slot.parentEl, { clearProps: 'all' }),
                    },
                    slotTime,
                  )
                }
                // Nested children use nested stagger (ms → sec); fallback to group stagger
                const nestedMs = parseFloat(
                  slot.nestedEl.getAttribute('data-stagger'),
                )
                const nestedStaggerSec = isNaN(nestedMs)
                  ? groupStaggerSec
                  : nestedMs / 1000
                Array.from(slot.nestedEl.children).forEach(
                  (nestedChild, nestedIndex) => {
                    tl.to(
                      nestedChild,
                      {
                        y: 0,
                        autoAlpha: 1,
                        duration: animDuration,
                        ease: animEase,
                        onComplete: () =>
                          gsap.set(nestedChild, { clearProps: 'all' }),
                      },
                      slotTime + nestedIndex * nestedStaggerSec,
                    )
                  },
                )
              }
            })
          }

          if (triggerType === 'load') {
            animateSlots()
          } else {
            ScrollTrigger.create({
              trigger: groupEl,
              start: triggerStart,
              once: true,
              onEnter: animateSlots,
            })
          }
        })

        // Cleanup function for matchMedia
        return () => {
          // Revert will be handled by the parent context
        }
      })

      // Mobile: Show elements immediately without animation
      mm.add('(max-width: 767px)', () => {
        showElementsImmediately(scope)

        return () => {
          // Cleanup not needed for mobile
        }
      })
    }, scope)

    contexts.set(scope, ctx)
  }

  function cleanup(scope) {
    if (contexts.has(scope)) {
      contexts.get(scope).revert()
      contexts.delete(scope)
    }
  }

  // Initialize Elements Reveal on Scroll
  document.addEventListener('DOMContentLoaded', () => {
    initContentRevealScroll(document)
  })

  // Shopify Theme Editor Events
  document.addEventListener('shopify:section:load', (e) => {
    initContentRevealScroll(e.target)
  })

  document.addEventListener('shopify:section:unload', (e) => {
    cleanup(e.target)
  })
})()
